<template>
    <div class="relative bg-white shadow-md rounded-xl p-4 m-4">
        <MonkeyLoader v-if="isLoading" />

        <h2 class="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Goods Receipt Entry</h2>
        <form @submit.prevent="saveGoodsReceipt">

            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="poNumber">PO #</label>
                    <v-select id="poNumber" v-model="form.poId" :options="poOptions" :reduce="po => po.id"
                        label="poNumber" placeholder="-- Ketik atau Pilih PO --" @option:selected="handlePoSelection"
                        class="input-border bg-white">
                    </v-select>
                </div>
                <div>
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="customerName">Customer
                        Name</label>
                    <input id="customerName" type="text" :value="selectedPoData.customerName"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                        disabled>
                </div>
                <div>
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="poType">PO Type</label>
                    <input id="poType" type="text" :value="selectedPoData.poType"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                        disabled>
                </div>
            </div>

            <h3 class="text-lg font-semibold mb-2 text-gray-700">Detail Item</h3>
            <div class="overflow-x-auto border rounded-lg">
                <table class="min-w-full bg-white">
                    <thead class="bg-blue-100">
                        <tr>
                            <th class="py-3 px-4 text-center text-xs font-bold text-blue-800 uppercase">Pilih</th>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase">Part Name</th>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase">Desc</th>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase">Qty PO</th>
                            <th class="py-3 px-4 text-center text-xs font-bold text-blue-800 uppercase">Qty GR</th>
                            <th class="py-3 px-4 text-center text-xs font-bold text-blue-800 uppercase">
                                Unit Price <span v-if="selectedPoData.discount > 0">({{ selectedPoData.discount
                                    }})</span>
                            </th>
                            <th class="py-3 px-4 text-center text-xs font-bold text-blue-800 uppercase">Total Amount
                            </th>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase w-32">Term</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="form.items.length === 0">
                            <td colspan="8" class="text-center py-4 text-gray-500">Pilih PO untuk menampilkan item.
                            </td>
                        </tr>
                        <tr v-for="item in form.items" :key="item.productId" class="odd:bg-white even:bg-blue-50">
                            <td class="py-2 px-4 text-center">
                                <input type="checkbox" v-model="item.selected">
                            </td>
                            <td class="py-2 px-4">{{ item.partName }}</td>
                            <td class="py-2 px-4">{{ item.partDesc }}</td>
                            <td class="py-2 px-4">{{ item.qty }}</td>
                            <td class="py-2 px-4 text-center">
                                <input type="number" min="0" :max="maxQtyForItem(item)"
                                    v-model.number="item.qtyGr" :disabled="!item.selected"
                                    class="input-border border rounded w-24 py-1 px-2 text-right">
                                <div class="text-[10px] text-gray-400">maks {{ maxQtyForItem(item) }}</div>
                            </td>
                            <td class="py-2 px-4 text-center">{{ formatCurrency(calculateDiscountedPrice(item)) }}</td>
                            <td class="py-2 px-4 text-center">{{ formatCurrency(item.qty *
                                calculateDiscountedPrice(item)) }}</td>
                            <td class="py-2 px-4">{{ selectedPoData.paymentTerm }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="flex items-end gap-4 mt-4">
                <div class="w-1/4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="grPercentageAmount">Persentase Amount
                        GR (%)</label>

                    <input id="grPercentageAmount" type="number" v-model.number="form.grPercentageAmount" min="0"
                        max="100" @input="validateGrPercentage"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700">

                    <p v-if="form.poId" class="text-xs text-gray-500 mt-1">
                        DP terdeteksi: {{ selectedPoData.dpPercentage || 0 }}% &middot;
                        Maksimal amount: {{ formatCurrency(maxAmount) }}
                    </p>
                    <p v-if="grSummaryData.pendingFinanceCount > 0" class="text-xs text-amber-600 mt-1">
                        {{ grSummaryData.pendingFinanceCount }} GR parsial untuk PO ini belum dikonfirmasi finance.
                    </p>
                </div>
                <div class="w-1/3">
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 gap-6 my-6">
                <div>
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="submitGrDate">Submit GR
                        Date</label>
                    <input id="submitGrDate" type="date" v-model="form.submitGrDate"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700">
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="md:col-span-2">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="remarks">Remarks</label>
                    <textarea id="remarks" v-model="form.remarks" rows="5"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"></textarea>
                </div>
                <div class="summary-box">
                    <h4 class="font-bold text-lg mb-3">Ringkasan GR</h4>

                    <div class="flex justify-between items-center text-sm mb-2">
                        <span class="text-gray-600">Total PO Amount:</span>
                        <span class="font-semibold">{{ formatCurrency(totalPoAmount) }}</span>
                    </div>

                    <div class="flex justify-between items-center text-blue-700 mb-2">
                        <span class="text-gray-600">Amount GR (Saat Ini):</span>
                        <span class="font-semibold">{{ calculatedGrAmountFormatted }}</span>
                    </div>

                    <hr class="my-2 border-t border-blue-200">

                    <div class="flex justify-between items-center text-sm">
                        <span class="font-bold">Balance GR:</span>
                        <span class="font-bold text-lg">{{ newBalanceGrFormatted }}</span>
                    </div>
                </div>
            </div>

            <div class="flex justify-center mt-8 pt-4 border-t gap-4">
                <button type="submit" class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-8 rounded-lg">
                    Save
                </button>
                <button type="button" @click="resetForm"
                    class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-8 rounded-lg">
                    Cancel
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

const API_ENDPOINTS = {
    purchaseOrders: 'https://crm-local.iotech.my.id/api/external/project?year2025&product=true',
    saveGoodsReceipt: `${window.location.origin}/api/goodsreceipts`,
    grStatusByPo: (poId) => `${window.location.origin}/api/grfiles/status/${poId}`
};

const getTodayDate = () => new Date().toISOString().split('T')[0];

const getInitialFormData = () => ({
    poId: '',
    submitGrDate: getTodayDate(),
    remarks: '',
    items: [],
    grPercentageAmount: null,
});

export default {
    name: 'GoodsReceiptEntry',
    components: {
        'v-select': vSelect,
        MonkeyLoader,
    },
    data() {
        return {
            form: getInitialFormData(),
            poOptions: [],
            fullPoDataList: [],
            selectedPoData: { customerName: '', poType: '', paymentTerm: '', discount: 0, dpPercentage: 0, invoices: [] },
            isLoading: true,
            grSummaryData: {
                totalPercentageGr: 0,
                totalBalanceGr: 0,
                pendingFinanceCount: 0,
                receivedItems: [],
                hasGrEntries: false,
            },
        };
    },
    computed: {
        // DIHAPUS: computed property 'discountPercentage'

        totalPoAmount() {
            const discountFactor = this.selectedPoData.discount || 0;
            return this.form.items.reduce((sum, item) => {
                const discountedPrice = item.unitPrice - (item.unitPrice * (discountFactor / 100));
                return sum + (item.qty * discountedPrice);
            }, 0);
        },
        selectedSubtotal() {
            return this.form.items
                .filter(i => i.selected && i.qtyGr > 0)
                .reduce((sum, i) => sum + (i.qtyGr * this.calculateDiscountedPrice(i)), 0);
        },
        calculatedGrAmount() {
            if (!this.selectedSubtotal || !this.form.grPercentageAmount) return 0;
            return (this.form.grPercentageAmount / 100) * this.selectedSubtotal;
        },
        maxAmount() {
            const cap = this.totalPoAmount * (100 - (this.selectedPoData.dpPercentage || 0)) / 100;
            return this.grSummaryData.hasGrEntries ? (this.grSummaryData.totalBalanceGr || 0) : cap;
        },
        calculatedGrAmountFormatted() {
            return this.formatCurrency(this.calculatedGrAmount);
        },
        newBalanceGrFormatted() {
            // maxAmount = sisa cap DP-aware (PO baru) atau balance tersisa (PO lama).
            return this.formatCurrency(this.maxAmount - this.calculatedGrAmount);
        },
    },
    methods: {
        calculateDiscountedPrice(item) {
            const discountFactor = this.selectedPoData.discount || 0;
            return item.unitPrice - (item.unitPrice * (discountFactor / 100));
        },
        maxQtyForItem(item) {
            const received = (this.grSummaryData.receivedItems || [])
                .find(r => r.partid === item.productId);
            const already = received ? Number(received.qtyReceived) : 0;
            return Math.max(0, Number(item.qty) - already);
        },
        formatCurrency(value) {
            if (value == null) return 'Rp 0';
            return `Rp ${new Intl.NumberFormat('id-ID').format(value)}`;
        },
        validateGrPercentage() {
            if (this.calculatedGrAmount > this.maxAmount + 0.01) {
                Swal.fire({
                    icon: 'warning',
                    title: `Amount melebihi maksimal ${this.formatCurrency(this.maxAmount)}`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                });
            }
        },
        async fetchGrStatus(poId) {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(API_ENDPOINTS.grStatusByPo(poId), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const statusData = response.data;
                this.grSummaryData = {
                    totalPercentageGr: statusData.totalPercentageGr || 0,
                    totalBalanceGr: statusData.totalBalanceGr,
                    pendingFinanceCount: statusData.pendingFinanceCount || 0,
                    receivedItems: statusData.receivedItems || [],
                    hasGrEntries: statusData.hasGrEntries || false,
                };
            } catch (error) {
                console.error(`Error fetching GR status for PO ID ${poId}:`, error);
                this.grSummaryData = { totalPercentageGr: 0, totalBalanceGr: 0, pendingFinanceCount: 0, receivedItems: [], hasGrEntries: false };
            }
        },
        async loadPoOptions() {
            this.isLoading = true;
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(API_ENDPOINTS.purchaseOrders, { headers: { Authorization: `Bearer ${token}` } });
                this.fullPoDataList = response.data;
                this.poOptions = response.data.map(po => ({
                    id: po.id,
                    poNumber: po.poNumber
                }));
            } catch (error) {
                console.error('Error fetching PO options:', error);
                Swal.fire('Error', 'Gagal memuat daftar Purchase Order.', 'error');
            } finally {
                this.isLoading = false;
            }
        },
        async handlePoSelection() {
            const poId = this.form.poId;
            if (!poId) {
                this.resetPoDetails();
                return;
            }
            const poData = this.fullPoDataList.find(po => po.id === poId);
            if (poData) {
                this.selectedPoData.customerName = poData.customerName;
                this.selectedPoData.poType = poData.poType?.typeName || 'N/A';
                this.selectedPoData.paymentTerm = poData.paymentTerm;
                this.selectedPoData.discount = poData.discount || 0;
                const invoices = Array.isArray(poData.invoices) ? poData.invoices : [];
                this.selectedPoData.invoices = invoices;
                this.selectedPoData.dpPercentage = invoices
                    .filter(inv => (inv.type || '').toUpperCase() === 'DP')
                    .reduce((sum, inv) => sum + (Number(inv.percentage) || 0), 0);
                this.form.items = (poData.products || []).map(p => ({ ...p, selected: false, qtyGr: 0 }));
                await this.fetchGrStatus(poId);
            }
        },
        async saveGoodsReceipt() {
            if (this.grSummaryData.pendingFinanceCount > 0) {
                const confirmation = await Swal.fire({
                    icon: 'warning',
                    title: 'Masih ada GR yang belum dikonfirmasi finance',
                    text: `PO ini punya ${this.grSummaryData.pendingFinanceCount} GR parsial yang belum diterima finance. Tetap simpan GR parsial baru?`,
                    showCancelButton: true,
                    confirmButtonText: 'Ya, Simpan GR Baru',
                    cancelButtonText: 'Batal',
                });
                if (!confirmation.isConfirmed) return;
            }
            const chosen = this.form.items.filter(i => i.selected && i.qtyGr > 0);
            if (!this.form.poId || this.form.grPercentageAmount <= 0 || !this.form.remarks || chosen.length === 0) {
                Swal.fire('Perhatian', 'Lengkapi PO, Persentase GR (>0), Remarks, dan pilih minimal satu item dengan qty.', 'warning');
                return;
            }
            if (this.calculatedGrAmount > this.maxAmount + 0.01) {
                Swal.fire('Perhatian', `Amount GR melebihi maksimal ${this.formatCurrency(this.maxAmount)}.`, 'warning');
                return;
            }

            const poData = this.fullPoDataList.find(po => po.id === this.form.poId);

            const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
            const now = new Date();
            const day = now.getDate();
            const month = monthNames[now.getMonth()];
            const year = now.getFullYear();
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const timestamp = `${day} ${month} ${year} ${hours}.${minutes}`;
            const userName = this.getUserFromToken();
            const formattedRemark = `${timestamp} , user : ${userName} , ${this.form.remarks.trim()}`;

            const payload = {
                pocustid: poData.id,
                pocustnum: poData.poNumber,
                custname: poData.customerName,
                potype: this.selectedPoData.poType,
                pototal: this.totalPoAmount,
                remark: formattedRemark,
                paymentterm: this.selectedPoData.paymentTerm,
                dppercentage: this.selectedPoData.dpPercentage || 0,
                grdetails: [{
                    submitdate: this.form.submitGrDate,
                    amountgr: this.calculatedGrAmount,
                    percentagegr: this.form.grPercentageAmount,
                    items: chosen.map(i => ({
                        partid: i.productId,
                        partname: i.partName,
                        partdesc: i.partDesc,
                        qtygr: i.qtyGr,
                        unitprice: this.calculateDiscountedPrice(i),
                        amountgr: i.qtyGr * this.calculateDiscountedPrice(i),
                    })),
                }],
                grpodetails: this.form.items.map(item => ({
                    partid: item.productId,
                    partname: item.partName,
                    partdesc: item.partDesc,
                    qtypo: item.qty,
                    amountpo: item.unitPrice,
                }))
            };
            try {
                this.isLoading = true;
                const token = localStorage.getItem('token');
                await axios.post(API_ENDPOINTS.saveGoodsReceipt, payload, { headers: { Authorization: `Bearer ${token}` } });
                Swal.fire('Berhasil', 'Data Goods Receipt berhasil disimpan.', 'success');
                this.resetForm();
            } catch (error) {
                console.error('Error saving Goods Receipt:', error);
                Swal.fire('Gagal', 'Terjadi kesalahan saat menyimpan data.', 'error');
            } finally {
                this.isLoading = false;
            }
        },
        getUserFromToken() {
            const token = localStorage.getItem('token');
            if (!token) return 'unknown';
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                return payload.sub || 'unknown';
            } catch (error) {
                console.error("Error decoding token:", error);
                return 'unknown';
            }
        },
        resetForm() {
            this.form = getInitialFormData();
            this.selectedPoData = { customerName: '', poType: '', paymentTerm: '', discount: 0, dpPercentage: 0, invoices: [] };
            this.grSummaryData = { totalPercentageGr: 0, totalBalanceGr: 0, pendingFinanceCount: 0, receivedItems: [], hasGrEntries: false };
        },
        resetPoDetails() {
            this.form.items = [];
            this.form.grPercentageAmount = null;
            this.selectedPoData = { customerName: '', poType: '', paymentTerm: '', discount: 0, dpPercentage: 0, invoices: [] };
            this.grSummaryData = { totalPercentageGr: 0, totalBalanceGr: 0, pendingFinanceCount: 0, receivedItems: [], hasGrEntries: false };
        },
    },
    mounted() {
        this.loadPoOptions();
    }
};
</script>

<style scoped>
.input-border {
    border: 1px solid #d2d6dc;
    border-radius: 0.375rem;
}

.input-border:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
    outline: none;
}

.summary-box {
    @apply bg-blue-50 border border-blue-200 rounded-lg p-4 h-full;
}

:deep(.vs__dropdown-toggle) {
    border: 1px solid #d2d6dc;
    border-radius: 0.375rem;
    padding: 4px 6px;
}

:deep(.vs--open .vs__dropdown-toggle) {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
}
</style>